import moment from 'moment';
import { Button, Grid } from '@material-ui/core';
import { Prompt, withRouter, useParams, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cloneDeep, isEmpty, isEqual, set } from 'lodash';

import SharingAgreementV2DetailLayout from './SharingAgreementV2DetailLayout';
import {
    flattenMessageTypes,
    patchMessageTypesRowIds,
    sharingRuleGroupsAreComplete,
    sharingRuleGroupsHaveAtLeastOneCompleteRow
} from './SharingRuleGroupsSection';
import TDialog from 'components/UI/Dialog/TDialog';
import Spinner from 'components/UI/Spinner/Spinner';
import { normalizeDeliverToOtherParties } from 'components/TrustCenter/store/utils/agreementV2Normalization';
import { requiresTmsSelection } from 'components/TrustCenter/store/utils/routingKeysUtils';
import * as Constants from 'constants/Constants';
import css from './SharingAgreement.module.css';

const DATE_FIELDS = ['validFrom', 'validTo'];
const OPTIONAL_FIELDS = ['validTo'];

/** @param {unknown} entry — string API name or `{ name }` from `provider-config` `providerCapabilities` */
function capabilityToApiName(entry) {
    if (entry == null || entry === '') return '';
    if (typeof entry === 'string') return entry.trim();
    if (typeof entry === 'object' && entry != null && entry.name != null) {
        return String(entry.name).trim();
    }
    return '';
}

const SharingAgreementDetail = ({
    loading,
    providers,
    agreementsV2,
    providerName,
    isReadOnly,
    createV2SharingAgreement,
    updateV2SharingAgreement,
    getProvidersV2,
    getSharingAgreementV2ById,
    selectedV2SharingAgreement,
    selectedProviderId,
    selectedAccount,
    session,
    history
}) => {
    const { sharingAgreementId, providerAccountId } = useParams();
    const location = useLocation();
    const [confirmOpen, setDialogOpen] = useState(false);
    const [agreement, setAgreement] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [dateError, setDateError] = useState({});
    const initialAgreementV2 = useRef(null);
    const createFormLocationKeyRef = useRef(null);

    useEffect(() => {
        if (!sharingAgreementId) {
            return;
        }
        setAgreement({});
        initialAgreementV2.current = null;
        getSharingAgreementV2ById(sharingAgreementId);
    }, [sharingAgreementId, getSharingAgreementV2ById]);

    useEffect(() => {
        if (!sharingAgreementId) {
            return;
        }
        if (isEmpty(selectedV2SharingAgreement)) {
            return;
        }
        if (String(selectedV2SharingAgreement.id) !== String(sharingAgreementId)) {
            return;
        }
        const _agreement = cloneDeep(selectedV2SharingAgreement);
        if (_agreement.messageTypes) {
            _agreement.messageTypes = patchMessageTypesRowIds(_agreement.messageTypes);
        }
        _agreement.deliverToOtherParties = normalizeDeliverToOtherParties(
            _agreement.deliverToOtherParties
        );
        setAgreement(_agreement);
        initialAgreementV2.current = _agreement;
    }, [selectedV2SharingAgreement, sharingAgreementId]);

    useEffect(() => {
        if (!isEmpty(providers)) {
            return;
        }
        getProvidersV2();
    }, [providers, getProvidersV2]);

    useEffect(() => {
        if (sharingAgreementId) {
            createFormLocationKeyRef.current = null;
            return;
        }

        const st = location.state || {};
        let providerAccount =
            st.newAgreementProviderAccount || selectedProviderId || providerAccountId || '';
        let providerAccountName = st.newAgreementProviderAccountName || '';

        const customerAccountName = selectedAccount?.name || session?.accountName || '';
        const customerAccount =
            selectedAccount?.id || session?.selected_accountId || session?.accountId || '';

        const isNewCreateNavigation = createFormLocationKeyRef.current !== location.key;
        if (isNewCreateNavigation) {
            createFormLocationKeyRef.current = location.key;
            if (!providerAccountName && providerAccount && !isEmpty(providers)) {
                providerAccountName =
                    (providers ?? []).find((p) => p.providerAccountId === providerAccount)
                        ?.providerName || '';
            }
            const draft = {
                providerAccount,
                providerAccountName,
                customerAccountName,
                customerAccount,
                deliverToOtherParties: false,
                direction: 'Outbound',
                validFrom: moment().format('YYYY-MM-DD')
            };
            setAgreement(draft);
            initialAgreementV2.current = draft;
            return;
        }

        setAgreement((prev) => {
            if (prev.id) {
                return prev;
            }
            const next = { ...prev };
            let changed = false;

            if (providerAccount && !prev.providerAccount) {
                next.providerAccount = providerAccount;
                changed = true;
            }
            const accountForName = next.providerAccount || providerAccount;
            if (accountForName && !next.providerAccountName && !isEmpty(providers)) {
                const name = (providers ?? []).find(
                    (p) => p.providerAccountId === accountForName
                )?.providerName;
                if (name) {
                    next.providerAccountName = name;
                    changed = true;
                }
            }
            if (!prev.customerAccountName && customerAccountName) {
                next.customerAccountName = customerAccountName;
                changed = true;
            }
            if (!prev.customerAccount && customerAccount) {
                next.customerAccount = customerAccount;
                changed = true;
            }

            if (!changed) {
                return prev;
            }
            next.deliverToOtherParties = normalizeDeliverToOtherParties(next.deliverToOtherParties);
            if (isEqual(prev, initialAgreementV2.current)) {
                initialAgreementV2.current = next;
            }
            return next;
        });
    }, [
        sharingAgreementId,
        location.key,
        location.state,
        selectedProviderId,
        providerAccountId,
        providers,
        selectedAccount?.id,
        selectedAccount?.name,
        session?.accountName,
        session?.accountId,
        session?.selected_accountId
    ]);

    const resetRequest = () => {
        setAgreement({ ...(initialAgreementV2.current ?? {}) });
        setDialogOpen(false);
    };

    useEffect(() => {
        setIsDirty(!isEqual(agreement, initialAgreementV2.current));
    }, [agreement]);

    const getProviderById = useCallback(
        (providerAccountId) => {
            return (providers ?? []).find(
                (provider) => provider.providerAccountId === providerAccountId
            );
        },
        [providers]
    );

    /**
     * Event type dropdown options from `GET .../provider-config` `providerCapabilities`.
     * Ordering and friendly labels for known track events are handled in SharingRuleGroupsSection;
     * types without a short label show the full API event name.
     */
    const eventTypeOptions = useMemo(() => {
        const cap = getProviderById(agreement.providerAccount)?.providerCapabilities;
        if (!Array.isArray(cap) || cap.length === 0) {
            return [];
        }
        const names = cap.map(capabilityToApiName).filter(Boolean);
        return [...new Set(names)];
    }, [agreement.providerAccount, getProviderById]);

    const providerDisplayName =
        agreement.providerAccountName ||
        getProviderById(agreement.providerAccount)?.providerName ||
        providerName ||
        '-';

    const customerDisplayName = agreement.customerAccountName || agreement.customerAccount || '-';

    const getHelperTextByField = useCallback((_agreement, field) => {
        if (field === 'validFrom') {
            return _agreement.validFrom &&
                moment(_agreement.validFrom).isSameOrAfter(moment(), 'day')
                ? ''
                : 'Must be today or after current date';
        } else if (field === 'validTo') {
            if (!_agreement.validTo) return '';
            return moment(_agreement.validTo).isAfter(moment(_agreement.validFrom))
                ? ''
                : 'Must be after valid from date';
        }
        return '';
    }, []);

    const onChange = (e) => {
        const { name, type, checked, value } = e.target;
        const nextValue = type === 'checkbox' ? checked : value;
        let _agreement = { ...agreement };
        set(_agreement, name, nextValue);
        if (name === 'direction' && nextValue === 'Outbound') {
            delete _agreement.tms;
        }
        setAgreement(_agreement);
        if (DATE_FIELDS.includes(name)) {
            setDateError((prev) => ({
                ...prev,
                [name]: getHelperTextByField(_agreement, name)
            }));
        }
    };

    const onMessageTypesChange = useCallback((messageTypes) => {
        setAgreement((prev) => ({ ...prev, messageTypes }));
    }, []);

    const disableSave = () => {
        if (!isDirty) return true;
        const rows = flattenMessageTypes(agreement.messageTypes);
        const sharingRuleGroupsComplete = sharingRuleGroupsAreComplete(rows);
        const isNewAgreement = isEmpty(agreement.id);
        const needsTms = isNewAgreement && requiresTmsSelection(agreement.direction);
        const sharingRuleGroupsOk =
            sharingRuleGroupsComplete &&
            (!isNewAgreement || sharingRuleGroupsHaveAtLeastOneCompleteRow(rows));
        const validFromInThePast =
            !isEmpty(agreement.validFrom) && moment(agreement.validFrom).isBefore(moment(), 'day');
        const isValidSharingAgreement = !(
            isEmpty(agreement.providerAccount) ||
            isEmpty(agreement.direction) ||
            isEmpty(agreement.validFrom) ||
            (isNewAgreement && validFromInThePast) ||
            (agreement.validTo &&
                moment(agreement.validTo).isSameOrBefore(moment(agreement.validFrom))) ||
            !sharingRuleGroupsOk ||
            (needsTms && isEmpty(agreement.tms))
        );
        return isValidSharingAgreement ? !isDirty : true;
    };

    return loading ? (
        <Spinner />
    ) : (
        <>
            <Grid container className={css.sharingAgreementContainer}>
                <Grid item xs={12}>
                    <SharingAgreementV2DetailLayout
                        providerDisplayName={providerDisplayName}
                        customerDisplayName={customerDisplayName}
                        agreement={agreement}
                        onChange={onChange}
                        onMessageTypesChange={onMessageTypesChange}
                        isReadOnly={isReadOnly}
                        dateError={dateError}
                        eventTypeOptions={eventTypeOptions}
                    />
                    {!isReadOnly && (
                        <div className={css.buttonContainer}>
                            <Button
                                variant="contained"
                                className={css.saveButton}
                                style={{
                                    opacity: disableSave() ? 0.5 : 1
                                }}
                                disabled={disableSave()}
                                onClick={() => {
                                    let _agreement = cloneDeep(agreement);
                                    _agreement = Object.keys(_agreement).reduce((acc, key) => {
                                        if (
                                            OPTIONAL_FIELDS.includes(key) &&
                                            isEmpty(_agreement[key])
                                        ) {
                                            return acc;
                                        }
                                        acc[key] = _agreement[key];
                                        return acc;
                                    }, {});
                                    const navigateToProviderAfterSave = (providerAccountId) => {
                                        if (providerAccountId) {
                                            history.push({
                                                pathname: `/connections/v2/provider/${providerAccountId}`,
                                                state: {
                                                    providerName:
                                                        _agreement.providerAccountName || ''
                                                }
                                            });
                                        }
                                    };

                                    if (_agreement.id) {
                                        const { tms, ...updatePayload } = _agreement;
                                        updateV2SharingAgreement(updatePayload, () => {
                                            initialAgreementV2.current = { ...updatePayload };
                                            setIsDirty(false);
                                            navigateToProviderAfterSave(
                                                updatePayload.providerAccount || selectedProviderId
                                            );
                                        });
                                    } else {
                                        createV2SharingAgreement(_agreement, (created) => {
                                            initialAgreementV2.current = created?.id
                                                ? { ..._agreement, ...created, id: created.id }
                                                : _agreement;
                                            setIsDirty(false);
                                            if (created?.id) {
                                                navigateToProviderAfterSave(
                                                    _agreement.providerAccount ||
                                                        selectedProviderId
                                                );
                                            }
                                        });
                                    }
                                }}
                            >
                                Save
                            </Button>
                            <Button
                                variant="contained"
                                color="default"
                                onClick={() => setDialogOpen(true)}
                            >
                                Reset
                            </Button>
                        </div>
                    )}
                </Grid>
            </Grid>
            <TDialog
                id={'reset_request'}
                open={confirmOpen}
                handleClose={() => setDialogOpen(false)}
                dialogContentText={Constants.UNSAVED_RESET_MESSAGE}
                dialogTitle={'Reset'}
                dialogActions={
                    <div>
                        <Button component="span" onClick={() => setDialogOpen(false)}>
                            Keep editing
                        </Button>
                        <Button component="span" onClick={resetRequest}>
                            Reset
                        </Button>
                    </div>
                }
            />
            <Prompt when={isDirty} message={Constants.UNSAVED_CHANGES_MESSAGE} />
        </>
    );
};

export default withRouter(SharingAgreementDetail);
